import { unref, watchEffect, ref, toRaw, watch } from "@nuxtjs/composition-api";

/**
 * @template T;
 * 
 * @template {import('vue').Ref<T>} R;
 * 
 * 
 * @param { T | () => T } defaultStateValue 
 * @param { object } option
 * @param { T | () => T  } [option.defaultValue]
 * @param { import('vue').Ref<T> | import('vue').Ref<import('vue').UnwrapRef<T>> } [option.value]
 * @param { (val: T, prevValue: T) => void } [option.onChange]
 * @param { (val:T) => T } [option.postState]
 * @returns {[ R, (val: T) => void ]}
 */
export default function useMergedState(
    defaultStateValue,
    option
) {
    const { defaultValue, value = ref() } = option || {};
    let initValue =
        typeof defaultStateValue === 'function' ? defaultStateValue() : defaultStateValue;
    if (value.value !== undefined) {
        initValue = unref(value);
    }
    if (defaultValue !== undefined) {
        initValue = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    }

    const innerValue = ref(initValue);
    const mergedValue = ref(initValue);
    watchEffect(() => {
        let val = value.value !== undefined ? value.value : innerValue.value;
        if (option.postState) {
            val = option.postState(val);
        }
        mergedValue.value = val;
    });

    function triggerChange(newValue) {
        const preVal = mergedValue.value;
        innerValue.value = newValue;
        if (toRaw(mergedValue.value) !== newValue && option.onChange) {
            option.onChange(newValue, preVal);
        }
    }

    // Effect of reset value to `undefined`
    watch(value, () => {
        innerValue.value = value.value;
    });

    return [mergedValue, triggerChange];
}
